import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IssueService } from 'app/cs/services/issue-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CS from 'app/cs/models/essette-api';

@autoinject()
export class MemberCustomerService extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected memberIssues: ReadonlyArray<CS.MemberIssue> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly service: IssueService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.loadMemberIssues();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadMemberIssues();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CS.Issue');
    }

    @apiCall
    private async loadMemberIssues(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes = [
            'assignedTo',
            'category',
            'category.subCategory',
            'center.location',
            'metadata.created.byUser',
            'status'
        ];

        const fields = [
            'assignedTo.description',
            'category.description',
            'category.subCategory.description',
            'center.location.description',
            'custom.lookup.*',
            'custom.text.*',
            'metadata.closed.timestamp',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'number',
            'status.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadIssuesByMember(this.memberId, options, includes, fields);
            this.memberIssues = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
